import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { API_URL as BASE_API_URL } from "../../config/apiConfig";
import { getDriverToken } from "../../utils/driverAuthUtils";

// Use centralized API configuration
const API_URL = `${BASE_API_URL}/wallet`;

const initialState = {
  wallet: null,
  transactions: [],
  withdrawalRequest: null,
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: "",
};

// Get driver wallet
export const getDriverWallet = createAsyncThunk(
  "wallet/getDriverWallet",
  async (driverId, thunkAPI) => {
    const maxRetries = 3;
    let retryCount = 0;

    while (retryCount < maxRetries) {
      try {
        const driverAuthState = thunkAPI.getState().driverAuth;

        // Use utility function to get token from multiple sources
        const token = getDriverToken(driverAuthState);

        if (!token) {
          return thunkAPI.rejectWithValue("Driver not authenticated");
        }


        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 15000, // 15 second timeout
        };

        const response = await axios.get(`${API_URL}/driver/${driverId}`, config);

        return response.data.data;
      } catch (error) {
        retryCount++;
        console.error(`Wallet API error (attempt ${retryCount}/${maxRetries}):`, error.message);

        // If it's a network error and we haven't exceeded retries, try again
        if (retryCount < maxRetries && (error.code === 'ECONNABORTED' || error.code === 'NETWORK_ERROR' || !error.response)) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }


        const message =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : error.message || "Network Error - Unable to load wallet";
        return thunkAPI.rejectWithValue(message);
      }
    }
  },
);

// Get driver transactions
export const getDriverTransactions = createAsyncThunk(
  "wallet/getDriverTransactions",
  async ({ driverId, page = 1, limit = 20 }, thunkAPI) => {
    const maxRetries = 3;
    let retryCount = 0;

    while (retryCount < maxRetries) {
      try {
        const driverAuthState = thunkAPI.getState().driverAuth;

        // Use utility function to get token from multiple sources
        const token = getDriverToken(driverAuthState);

        if (!token) {
          return thunkAPI.rejectWithValue("Driver not authenticated");
        }


        const config = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 15000, // 15 second timeout
        };

        const requestUrl = `${API_URL}/driver/${driverId}/transactions?page=${page}&limit=${limit}`;

        const response = await axios.get(requestUrl, config);

        return response.data.data;
      } catch (error) {
        retryCount++;
        console.error(`Transactions API error (attempt ${retryCount}/${maxRetries}):`, error.message);

        // If it's a network error and we haven't exceeded retries, try again
        if (retryCount < maxRetries && (error.code === 'ECONNABORTED' || error.code === 'NETWORK_ERROR' || !error.response)) {
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        }


        const message =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : error.message || "Network Error - Unable to load transactions";
        return thunkAPI.rejectWithValue(message);
      }
    }
  },
);

// Request withdrawal
export const requestWithdrawal = createAsyncThunk(
  "wallet/requestWithdrawal",
  async ({ driverId, amount, paymentMethod, accountDetails }, thunkAPI) => {
    try {
      const driverAuthState = thunkAPI.getState().driverAuth;
      const token = driverAuthState.token || driverAuthState.driver?.token;
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.post(
        `${API_URL}/driver/request-withdrawal`,
        { driverId, amount, paymentMethod, accountDetails },
        config,
      );
      return response.data.data;
    } catch (error) {
      const message =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message;
      return thunkAPI.rejectWithValue(message);
    }
  },
);

// Pay commission
export const payCommission = createAsyncThunk(
  "wallet/payCommission",
  async ({ driverId, amount, paymentMode, referenceId }, thunkAPI) => {
    try {
      const driverAuthState = thunkAPI.getState().driverAuth;
      const token = driverAuthState.token || driverAuthState.driver?.token;
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.post(
        `${API_URL}/driver/pay-commission`,
        { driverId, amount, paymentMode, referenceId },
        config,
      );
      return response.data.data;
    } catch (error) {
      const message =
        error.response && error.response.data.message
          ? error.response.data.message
          : error.message;
      return thunkAPI.rejectWithValue(message);
    }
  },
);

export const walletSlice = createSlice({
  name: "wallet",
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false;
      state.isError = false;
      state.isSuccess = false;
      state.message = "";
    },
    clearWithdrawalRequest: (state) => {
      state.withdrawalRequest = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Get driver wallet
      .addCase(getDriverWallet.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getDriverWallet.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.wallet = action.payload;
      })
      .addCase(getDriverWallet.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Get driver transactions
      .addCase(getDriverTransactions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getDriverTransactions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.transactions = action.payload.transactions;
      })
      .addCase(getDriverTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Request withdrawal
      .addCase(requestWithdrawal.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(requestWithdrawal.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.withdrawalRequest = action.payload;
        state.message = "Withdrawal request submitted successfully";
        // Update wallet balance and pending withdrawal
        if (state.wallet) {
          state.wallet.pendingWithdrawal = action.payload;
        }
      })
      .addCase(requestWithdrawal.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Pay commission
      .addCase(payCommission.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(payCommission.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.message = "Commission payment processed successfully";
        // Update wallet balance
        if (state.wallet) {
          state.wallet.balance = action.payload.newBalance;
        }
      })
      .addCase(payCommission.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      });
  },
});

export const { reset, clearWithdrawalRequest } = walletSlice.actions;
export default walletSlice.reducer;
